import { logger } from '../../features/logger';
import { apiResponseHandler } from '../../utils/hooksLib.jsx';

class APIClient {
  /**
   * Create an APIClient instance
   * @param {string} accessToken - The bearer token you want to pass in the headers of each request
   * @param {Object} oktaAuth - The OAuth AuthService class
   */
  constructor({ accessToken } = {}, oktaAuth) {
    this.accessToken = accessToken;
    this.oktaAuth = oktaAuth;
  }

  /**
   * @param {string} url - The url you want to send a GET request
   * @returns {Promise} - Promise with the api data requested if the response is ok
   * @throws - Will throw error if request fails or response is not ok
   */
  async get(url, sendRawData) {
    const response = await fetch(url, {
      method: 'get',
      headers: {
        Authorization: `Bearer ${this.accessToken}`
      }
    });

    if (sendRawData) {
      return response;
    }
    // return response;
    const resp = await apiResponseHandler(response, this.oktaAuth);
    if (response.ok) {
      return resp;
    }

    throw new Error(`${response.status}: ${resp.message}`);
  }

  async getSmartyAPI(url, sendRawData) {
    const response = await fetch(url, {
      method: 'get'
    });

    if (sendRawData) {
      return response;
    }
    // return response;
    const text = await response.text();
    let resp;
    try {
      resp = JSON.parse(text);
    } catch (e) {
      logger.error('Error parsing Smarty API response:', e);
      resp = { message: text };
    }

    if (response.ok) {
      return resp;
    }

    throw new Error(`${response.status}: ${resp.message}`);
  }

  /**
   * @param {string} url - The url you want to send a PUT request
   * @param {object} body - The request body you want to send
   * @returns {Promise} - Promise with the api data requested if the response is ok
   * @throws - Will throw error if request fails or response is not ok
   */
  async put(url, body) {
    const response = await fetch(url, {
      method: 'put',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`
      }
    });

    const resp = await apiResponseHandler(response, this.oktaAuth);
    if (response.ok) {
      return resp;
    }

    throw new Error(`${response.status}: ${resp.message}`);
  }

  /**
   * @param {string} url - The url you want to send a POST
   * @param {object} body - The request body you want to send
   * @returns {Promise} - Promise with the api response if the response is ok
   * @throws - Will throw error if request fails or response is not ok
   */
  async post(url, body) {
    const response = await fetch(url, {
      method: 'post',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`
      }
    });

    const resp = await apiResponseHandler(response, this.oktaAuth);
    if (response.ok) {
      return resp;
    }

    throw new Error(`${response.status}: ${resp.message}`);
  }

  /**
   * @param {string} url - The url you want to send a DELETE request
   * @returns {Promise} - Promise if the response is ok
   * @throws - Will throw error if request fails or response is not ok
   */
  async delete(url) {
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        Authorization: `Bearer ${this.accessToken}`
      }
    });

    const resp = await apiResponseHandler(response, this.oktaAuth);
    if (response.ok) {
      return resp;
    }

    throw new Error(`${response.status}: ${resp.message}`);
  }

  /**
   * @param {string} url - The url you want to send a POST for upload
   * @param {Object} data - The data you want to send (json or FormData)
   * @returns {Promise} - Promise with the api response if the response is ok
   * @throws - Will throw error if request fails or response is not ok
   */
  async upload(url, data) {
    const response = await fetch(url, {
      method: 'post',
      body: data,
      headers: {
        Authorization: `Bearer ${this.accessToken}`
      }
    });

    const resp = await apiResponseHandler(response, this.oktaAuth);
    if (response.ok) {
      return resp;
    }

    throw new Error(`${response.status}: ${resp.message}`);
  }
}

export default APIClient;
